// useZoneDraw — Zone Finder's "drag a zone on the pitch" gesture. A 1:1 port of the legacy
// wireZoneDraw (js/renderers-zonefinder.js), kept apart from usePitchAreaZoom (04m fork 2A)
// because the two gestures differ in small, deliberate ways:
// - a PLAIN CLICK (no drag) means "clear the zone" here; on Shot/Pass Map it does nothing;
// - one flag, `dataset.zoneDragging`, set only once past the threshold and cleared on
//   release — there's no press-time flag (the legacy has none) and no click to guard;
// - the zone commits the same way (kit areaFromDrag: clamped, ≥ 3 units each way).
// The shared maths (threshold, clamp, overlay box, commit rule) is the kit's.
//
// The dashed overlay is made and removed by hand in `.event-pitch-wrap`, as the legacy: it
// only lives mid-drag and moves on every pointermove, so it isn't React state.

import { useEffect, useRef, type RefObject } from "react";
import { areaFromDrag, areaOverlayBox, clampToPitch, isAreaDrag, type EventArea } from "../engines/event-map-kit.js";
import { hideTooltip } from "../canvas/tooltip.js";

interface Handlers {
  /** A finished drag big enough to be a zone. */
  onZone: (zone: EventArea) => void;
  /** A press + release without a drag. */
  onPlainClick: () => void;
}

export function useZoneDraw(svgRef: RefObject<SVGSVGElement | null>, handlers: Handlers): void {
  // The latest handlers, without re-wiring the listeners on every render.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const svg = svgRef.current;
    const wrapEl = svg?.closest<HTMLElement>(".event-pitch-wrap");
    if (!svg || !wrapEl) return;
    let dragStart: { x: number; y: number } | null = null;
    let dragging = false;
    let overlayEl: HTMLDivElement | null = null;

    const removeOverlay = () => {
      overlayEl?.remove();
      overlayEl = null;
    };
    // The zone lives in the rotated group's local (pitch) space.
    const screenCtm = () => (svg.querySelector<SVGGElement>(".pitch-rotate") ?? svg).getScreenCTM();
    const toLocal = (ctm: DOMMatrix, clientX: number, clientY: number) => {
      const pt = svg.createSVGPoint();
      pt.x = clientX;
      pt.y = clientY;
      return pt.matrixTransform(ctm.inverse());
    };
    const toScreen = (ctm: DOMMatrix, p: { x: number; y: number }) => {
      const pt = svg.createSVGPoint();
      pt.x = p.x;
      pt.y = p.y;
      return pt.matrixTransform(ctm);
    };

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      hideTooltip();
      dragStart = { x: e.clientX, y: e.clientY };
      dragging = false;
      // So a release past the pitch edge still lands here.
      svg.setPointerCapture(e.pointerId);
    };

    const onMove = (e: PointerEvent) => {
      if (!dragStart) return;
      if (!dragging) {
        if (!isAreaDrag(e.clientX - dragStart.x, e.clientY - dragStart.y)) return;
        dragging = true;
        // The dot-tooltip listener stands down while this is set, so drawing a zone and
        // hovering a dot never fight over the same pointer movement.
        svg.dataset.zoneDragging = "1";
        hideTooltip();
        overlayEl = document.createElement("div");
        overlayEl.className = "event-area-select-overlay";
        wrapEl.appendChild(overlayEl);
      }
      const ctm = screenCtm();
      if (!ctm || !overlayEl) return;
      // Clamp both corners to the pitch, then back to screen: the preview stops at the
      // pitch edge however far the cursor goes.
      const a = toScreen(ctm, clampToPitch(toLocal(ctm, dragStart.x, dragStart.y)));
      const b = toScreen(ctm, clampToPitch(toLocal(ctm, e.clientX, e.clientY)));
      const box = areaOverlayBox(a, b, wrapEl.getBoundingClientRect());
      overlayEl.style.left = `${box.left}px`;
      overlayEl.style.top = `${box.top}px`;
      overlayEl.style.width = `${box.width}px`;
      overlayEl.style.height = `${box.height}px`;
    };

    const onUp = (e: PointerEvent) => {
      if (!dragStart) return;
      const start = dragStart;
      const wasDragging = dragging;
      dragStart = null;
      dragging = false;
      delete svg.dataset.zoneDragging;
      removeOverlay();
      if (!wasDragging) {
        handlersRef.current.onPlainClick();
        return;
      }
      const ctm = screenCtm();
      if (!ctm) return;
      const zone = areaFromDrag(toLocal(ctm, start.x, start.y), toLocal(ctm, e.clientX, e.clientY));
      if (zone) handlersRef.current.onZone(zone);
    };

    svg.addEventListener("pointerdown", onDown);
    svg.addEventListener("pointermove", onMove);
    svg.addEventListener("pointerup", onUp);
    return () => {
      svg.removeEventListener("pointerdown", onDown);
      svg.removeEventListener("pointermove", onMove);
      svg.removeEventListener("pointerup", onUp);
      removeOverlay();
    };
  }, [svgRef]);
}
